import { cn } from "helpers/classNames";
import { Link, type LinkProps, useNavigate } from "@tanstack/react-router";
import { ComponentType, SVGProps } from "react";

export type OrgTabItem = {
  label: string;
  value: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isActive: boolean;
  link: Pick<LinkProps, "to" | "params" | "search">;
};

type Props = {
  items: OrgTabItem[];
  className?: string;
};

export const OrgTabBar = ({ items, className }: Props) => {
  const navigate = useNavigate();

  return (
    <div className={cn(className)}>
      {/* Mobile: native select keeps routing + accessibility simple */}
      <div className="sm:hidden">
        <label htmlFor="org-tab-select" className="sr-only">
          Välj flik
        </label>
        <select
          id="org-tab-select"
          value={items.find((t) => t.isActive)?.value}
          onChange={(e) => {
            const selected = items.find((t) => t.value === e.target.value);
            if (selected) navigate(selected.link);
          }}
          className="w-full rounded-xl border-0 bg-white py-2.5 pl-3.5 pr-10 text-sm font-medium text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-blue-500"
        >
          {items.map((tab) => (
            <option key={tab.value} value={tab.value}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop: segmented pill bar */}
      <div className="hidden sm:block">
        <nav
          aria-label="Organisationsflikar"
          className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm"
        >
          {items.map((tab) => {
            const Icon = tab.icon;
            return (
              <Link
                key={tab.value}
                {...tab.link}
                aria-current={tab.isActive ? "page" : undefined}
                className={cn(
                  "group inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors",
                  tab.isActive
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
                )}
              >
                <Icon
                  className={cn(
                    "h-4 w-4",
                    tab.isActive
                      ? "text-white"
                      : "text-slate-400 group-hover:text-slate-600",
                  )}
                  aria-hidden="true"
                />
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
